import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { PREFERRED_UNIT_SYSTEMS, PreferredUnitSystem } from '@frontend/api';
import {
  AxisAlignedDimensionsIcon,
  Divider,
  Flex,
  MeasureIcon,
  OptimalBoundingBoxIcon,
  OutlineCircleQuestionIcon,
  PMIIcon,
  ResetViewIcon,
  SelectionToolIcon,
  Tooltip,
  FormControl,
  FormLabel,
  NumberInput,
  NumberInputField,
  HStack,
  Text,
  GearIcon,
  Segment,
  SegmentControl,
  Switch,
} from '@frontend/legacy-components';

import {
  ViewerButton,
  ViewerButtonTooltip,
  ViewerControlsInfoRow,
  ViewerPopoverButton,
} from 'modules/common/viewer/components';
import { viewer } from 'modules/common/viewer/data/actions';
import {
  getActivePartFileUuid,
  getIsLegacyDxfViewerForPartFile,
} from 'modules/common/viewer/data/reducer';

import { useAppSelector } from 'store/hooks';

import { isMobileBrowser } from 'utils/browserUtils';

import { ViewerToolsBar } from '../components/ViewerToolsBar';
import { useDxfViewerSession } from './DxfViewerContext';
import { useDisplayOptions } from './hooks/useDisplayOptions';
import type { OverlayKey } from './session/types';
import { useDxfLoad, useReadableStore } from './session/useSessionState';

/**
 * 2D viewer controls (DXF/DWG files) — body only; popover title is shared with 3D.
 */
export const DxfViewerControlsInfoRows = ({
  isMobile,
}: {
  isMobile: boolean;
}) => (
  <Flex direction="column" gap={0.5} minW="260px">
    <ViewerControlsInfoRow
      title="Zoom:"
      value={isMobile ? 'Pinch' : 'Scroll'}
    />
    <ViewerControlsInfoRow
      title="Pan:"
      value={isMobile ? 'Drag finger' : 'Drag MMB or Space + drag'}
    />
    <ViewerControlsInfoRow title="Select:" value="Click or drag a box" />
    <ViewerControlsInfoRow title="Add / remove:" value="Shift / Alt" />
    <ViewerControlsInfoRow title="Select one edge:" value="Double-click" />
    <ViewerControlsInfoRow title="Rotate:" value="Use rotate buttons" />
    <ViewerControlsInfoRow
      title="Reset view:"
      value={isMobile ? 'Double tap' : 'Reset button'}
    />
  </Flex>
);
DxfViewerControlsInfoRows.displayName = 'DxfViewerControlsInfoRows';

export const DxfViewerTools = () => {
  const isMobile = isMobileBrowser();
  const session = useDxfViewerSession();
  const dispatch = useDispatch();

  // Discrete subscriptions: this toolbar re-renders when a person changes
  // something, and never while the drawing is being panned or zoomed.
  const load = useDxfLoad(session);
  const isSceneReady = load.status === 'ready';
  const hasPmi = isSceneReady ? load.model.hasPmi : false;
  const tool = useReadableStore(session.tools, (current) => current);
  const overlays = useReadableStore(session.overlays, (current) => current);

  const selectTool = useCallback(() => session.tools.set('select'), [session]);
  const measureTool = useCallback(
    () => session.tools.set('measure'),
    [session],
  );

  const toggleOverlay = useCallback(
    (key: OverlayKey) => session.overlays.toggle(key),
    [session],
  );

  const onResetView = useCallback(() => {
    session.viewport.reset();
  }, [session]);

  /* Display Options */
  const {
    dimensionalPrecision,
    preferredUnitSystem,
    handleUnitsChange,
    handlePrecisionChange,
  } = useDisplayOptions();

  /* Legacy DXF Viewer Toggle */
  const activePartFileUuid = useAppSelector(getActivePartFileUuid);
  const isLegacyDxfViewer = useAppSelector((state) =>
    getIsLegacyDxfViewerForPartFile(state, activePartFileUuid),
  );
  const setIsLegacyDxfViewer = useCallback(
    (nextIsLegacyDxfViewer: boolean) => {
      if (!activePartFileUuid) {
        return;
      }
      dispatch(
        viewer.setLegacyDxfViewerForPartFile({
          partFileUuid: activePartFileUuid,
          isLegacyDxfViewer: nextIsLegacyDxfViewer,
        }),
      );
    },
    [dispatch, activePartFileUuid],
  );

  return (
    <ViewerToolsBar>
      {/* Select and Measure are mutually exclusive: exactly one is lit, and
            switching discards the selection and any measurement. */}
      <Tooltip
        placement="bottom"
        label={
          <ViewerButtonTooltip
            firstLine="Select"
            secondLine="Drag a box to select; double-click for one edge"
          />
        }
      >
        <ViewerButton
          isActive={tool === 'select'}
          aria-label="Select"
          onClick={selectTool}
          disabled={!isSceneReady}
        >
          <SelectionToolIcon boxSize={3} />
        </ViewerButton>
      </Tooltip>
      <Tooltip
        placement="bottom"
        label={
          <ViewerButtonTooltip
            firstLine="Measure"
            secondLine="Click two points; snaps to ends, midpoints and centres"
          />
        }
      >
        <ViewerButton
          isActive={tool === 'measure'}
          aria-label="Measure"
          onClick={measureTool}
          disabled={!isSceneReady}
        >
          <MeasureIcon boxSize={3} />
        </ViewerButton>
      </Tooltip>
      <Divider orientation="vertical" h={4} />
      {/* Axis Aligned Dimensions Tool */}
      <Tooltip
        placement="bottom"
        label={
          <ViewerButtonTooltip
            firstLine="Axis aligned dimensions"
            secondLine="Overall width and height of visible geometry"
          />
        }
      >
        <ViewerButton
          isActive={overlays.axisDimensions}
          aria-label="Axis aligned dimensions"
          onClick={() => toggleOverlay('axisDimensions')}
          disabled={!isSceneReady}
        >
          <AxisAlignedDimensionsIcon boxSize={3} />
        </ViewerButton>
      </Tooltip>
      {/* Optimal Bounding Box Tool */}
      <Tooltip
        placement="bottom"
        label={
          <ViewerButtonTooltip
            firstLine="Optimal bounding box dimensions"
            secondLine="Smallest-area outline of visible geometry"
          />
        }
      >
        <ViewerButton
          isActive={overlays.optimalBoundingBox}
          aria-label="Optimal bounding box dimensions"
          onClick={() => toggleOverlay('optimalBoundingBox')}
          disabled={!isSceneReady}
        >
          <OptimalBoundingBoxIcon boxSize={3} />
        </ViewerButton>
      </Tooltip>
      {/* PMI — disabled, and saying why, when the file carries none. */}
      <Tooltip
        placement="bottom"
        label={
          <ViewerButtonTooltip
            firstLine="Dimensions and annotation"
            secondLine={
              hasPmi
                ? 'The drawing’s own dimension entities'
                : 'This file carries no dimension entities'
            }
          />
        }
      >
        <ViewerButton
          isActive={overlays.pmi}
          aria-label="Dimensions and annotation"
          onClick={() => toggleOverlay('pmi')}
          disabled={!isSceneReady || !hasPmi}
        >
          <PMIIcon boxSize={3} />
        </ViewerButton>
      </Tooltip>
      <Divider orientation="vertical" h={4} />
      {/* Reset View Tool */}
      <Tooltip
        placement="bottom"
        label={<ViewerButtonTooltip firstLine="Reset view" />}
      >
        <ViewerButton
          aria-label="Reset view"
          onClick={onResetView}
          disabled={!isSceneReady}
        >
          <ResetViewIcon boxSize={3} />
        </ViewerButton>
      </Tooltip>
      {/* Controls Info Tool */}
      <ViewerPopoverButton
        title="Viewer controls"
        icon={<OutlineCircleQuestionIcon boxSize={3} />}
      >
        <DxfViewerControlsInfoRows isMobile={isMobile} />
      </ViewerPopoverButton>
      {/* Display Options Tool */}
      <ViewerPopoverButton
        title="Display Options"
        icon={<GearIcon boxSize={3} />}
      >
        <>
          <FormControl mb={1.5}>
            <FormLabel>Units</FormLabel>
            <SegmentControl
              name="dxf-viewer-display-units"
              size="sm"
              value={preferredUnitSystem}
              onChange={(value) =>
                handleUnitsChange(value as PreferredUnitSystem)
              }
              w="100%"
            >
              <Segment value={PREFERRED_UNIT_SYSTEMS.IMPERIAL}>
                Imperial
              </Segment>
              <Segment value={PREFERRED_UNIT_SYSTEMS.METRIC}>Metric</Segment>
            </SegmentControl>
          </FormControl>
          <FormControl mb={1.5}>
            <FormLabel>Dimensional precision</FormLabel>
            <HStack>
              <Text fontSize="sm">Decimal places</Text>
              <NumberInput
                name="dxf-viewer-dimensional-precision"
                clampValueOnBlur
                focusInputOnChange
                max={6}
                min={0}
                step={1}
                value={dimensionalPrecision}
                onChange={handlePrecisionChange}
                pattern="[0-9]*(.[0-9]+)?"
                size="sm"
                width="auto"
              >
                <NumberInputField width={6} />
              </NumberInput>
            </HStack>
          </FormControl>
          <FormControl mb={1.5}>
            <FormLabel>Legacy DXF Viewer</FormLabel>
            <HStack>
              <Text fontSize="sm">Legacy DXF Viewer</Text>
              <Switch
                name="dxf-viewer-legacy-viewer-toggle"
                size="sm"
                isChecked={isLegacyDxfViewer}
                onChange={(event) => setIsLegacyDxfViewer(event.target.checked)}
              />
            </HStack>
          </FormControl>
        </>
      </ViewerPopoverButton>
    </ViewerToolsBar>
  );
};
